import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { computed, inject, Injectable } from '@angular/core';
import { IrisDMSContextMenuRequestI } from '../models/IrisDMSContextMenu';
import {
  DmsChangeCurrentProject,
} from '@iris/common/modules/dms/sandbox/actions/dms-global.actions';
import { IrisLocalStorageService } from '@iris/common/services/utils/iris-local-storage.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { IrisContentViewType } from '../models/IrisDmsViewType';
import { IrisDmsGlobalStore } from '../store/dms-global.signal-store';
import { IrisDmsFolderViewTab, IrisDmsFolderViewTabType } from '../utils/dms-routing.utils';
import { IrisDmsRoutingService } from '../services/dms-routing.service';

@Injectable({
  providedIn: 'root',
})
export class IrisDmsGlobalSandbox {
  private readonly store = inject(Store);
  private readonly localStorageService = inject(IrisLocalStorageService);

  private readonly dmsGlobalStore = inject(IrisDmsGlobalStore);
  private readonly dmsRoutingService = inject(IrisDmsRoutingService);

  private readonly contentViewPageSizeStorageKey = 'dmsContentViewPageSize';

  readonly deeplLanguages = this.dmsGlobalStore.deeplLanguages;
  readonly currentProjectIdModal = this.dmsGlobalStore.currentProjectIdModal;
  readonly checkAll = this.dmsGlobalStore.checkAll;
  readonly checkAll$ = toObservable(this.checkAll);

  readonly contentViewType = this.dmsGlobalStore.contentViewType;
  readonly isTileView = computed(() => this.contentViewType() === IrisContentViewType.Tile);
  readonly isListView = computed(() => this.contentViewType() === IrisContentViewType.List);

  readonly contextMenuOpenRequest$ = new Subject<IrisDMSContextMenuRequestI>();

  readonly selectedFolderId = this.dmsGlobalStore.selectedFolderId;

  get pageSize(): number {
    return this.localStorageService.getItem(this.contentViewPageSizeStorageKey);
  }

  set pageSize(value: number) {
    this.localStorageService.setItem(this.contentViewPageSizeStorageKey, value);
  }

  setCheckAll(checkAll: boolean): void {
    this.dmsGlobalStore.setCheckAll(checkAll);
  }

  getDeeplLanguages(): void {
    this.dmsGlobalStore.fetchDeeplLanguages$();
  }

  selectDocumentsTab(skipRouting = false): void {
    this.setFolderViewSelectedTab(IrisDmsFolderViewTab.Documents, skipRouting);
  }

  selectTrashBinTab(skipRouting = false): void {
    this.setFolderViewSelectedTab(IrisDmsFolderViewTab.Trash, skipRouting);
  }

  selectTagsTab(skipRouting = false): void {
    this.setFolderViewSelectedTab(IrisDmsFolderViewTab.Tags, skipRouting);
  }

  selectSystemFoldersTab(skipRouting = false): void {
    this.setFolderViewSelectedTab(IrisDmsFolderViewTab.SystemFolders, skipRouting);
  }

  setFolderViewSelectedTab(tab: IrisDmsFolderViewTab, skipRouting = false): void {
    this.dmsGlobalStore.setFolderViewSelectedTab(tab);
    const folderViewSelectedTab = this.dmsGlobalStore.folderViewSelectedTab();
    if (!skipRouting) {
      switch (IrisDmsFolderViewTab[folderViewSelectedTab] as IrisDmsFolderViewTabType) {
        case IrisDmsFolderViewTabType.Trash:
          void this.dmsRoutingService.openTrashBinViewPage();
          break;
        case IrisDmsFolderViewTabType.Tags:
          void this.dmsRoutingService.openTagsViewPage();
          break;
        case IrisDmsFolderViewTabType.SystemFolders:
          void this.dmsRoutingService.openSystemFoldersViewPage();
          break;
        case IrisDmsFolderViewTabType.Documents:
          void this.dmsRoutingService.openFoldersViewPage();
      }
    }
  }

  changeCurrentProject(projectId: number, doNotSelectFolder: boolean): void {
    this.store.dispatch(DmsChangeCurrentProject({
      projectId,
      doNotSelectFolder,
    }));
  }

  setContentViewType(viewType: IrisContentViewType): void {
    this.dmsGlobalStore.setContentViewType(viewType);
  }

  resetSelectedFolderId(): void {
    this.dmsGlobalStore.resetSelectedFolderId();
  }
}
